import { useLocale } from "@/context/LanguageContext";
import { cn } from "@/lib/utils";
import { lastNDays } from "../utils/dateRange";

interface Props {
  events: string[];
  event: string;
  onEventChange: (event: string) => void;
  eventLabel: (event: string) => string;
  from: string;
  to: string;
  onRangeChange: (from: string, to: string) => void;
}

const PRESETS: { days: number; labelKey: string }[] = [
  { days: 7, labelKey: "analytics.range.last7" },
  { days: 30, labelKey: "analytics.range.last30" },
  { days: 90, labelKey: "analytics.range.last90" },
];

const fieldClass =
  "h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";

export function AnalyticsControls({
  events,
  event,
  onEventChange,
  eventLabel,
  from,
  to,
  onRangeChange,
}: Props) {
  const { t } = useLocale();

  return (
    <div className="flex flex-wrap items-end gap-4 rounded-lg border border-border bg-card p-4">
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">{t("analytics.controls.event")}</span>
        <select
          value={event}
          onChange={(e) => onEventChange(e.target.value)}
          className={fieldClass}
        >
          {events.map((ev) => (
            <option key={ev} value={ev}>
              {eventLabel(ev)}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">{t("analytics.controls.from")}</span>
        <input
          type="date"
          value={from}
          max={to}
          onChange={(e) => onRangeChange(e.target.value, to)}
          className={fieldClass}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">{t("analytics.controls.to")}</span>
        <input
          type="date"
          value={to}
          min={from}
          onChange={(e) => onRangeChange(from, e.target.value)}
          className={fieldClass}
        />
      </label>

      <div className="flex items-center gap-1">
        {PRESETS.map((preset) => (
          <button
            key={preset.days}
            type="button"
            onClick={() => {
              const range = lastNDays(preset.days);
              onRangeChange(range.from, range.to);
            }}
            className={cn(
              "rounded-md border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground",
            )}
          >
            {t(preset.labelKey)}
          </button>
        ))}
      </div>
    </div>
  );
}
